import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusCard } from "./StatusCard";
import type { ClaimStatusTemplate } from "@/api/generated/client/models/ClaimStatusTemplate";
import type { TaskTemplate } from "@/api/generated/client/models/TaskTemplate";
import { useClaimPipelines } from "@/hooks/use-claim-pipelines";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { PipelineDialog, type PipelineDraft } from "./PipelineDialog";

interface StatusWithTasks extends ClaimStatusTemplate {
  taskTemplates: TaskTemplate[];
}

interface DraggableStatusItemProps {
  status: StatusWithTasks;
  onEdit: (status: ClaimStatusTemplate) => void;
  onDelete: (status: ClaimStatusTemplate) => void;
  onAddTask: (status: ClaimStatusTemplate) => void;
  onEditTask: (task: TaskTemplate, status: ClaimStatusTemplate) => void;
  onDeleteTask: (task: TaskTemplate, status: ClaimStatusTemplate) => void;
  isAdmin: boolean;
}

function DraggableStatusItem({
  status,
  onEdit,
  onDelete,
  onAddTask,
  onEditTask,
  onDeleteTask,
  isAdmin
}: DraggableStatusItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {isAdmin && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-accent rounded"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      )}
      <div className={isAdmin ? "pl-8" : ""}>
        <StatusCard
          status={status}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddTask={onAddTask}
          onEditTask={onEditTask}
          onDeleteTask={onDeleteTask}
          isAdmin={isAdmin}
        />
      </div>
    </div>
  );
}

interface DraggableStatusListProps {
  statuses: StatusWithTasks[];
  onEdit: (status: ClaimStatusTemplate) => void;
  onDelete: (status: ClaimStatusTemplate) => void;
  onReorder: (statuses: StatusWithTasks[]) => void;
  onAddTask: (status: ClaimStatusTemplate) => void;
  onEditTask: (task: TaskTemplate, status: ClaimStatusTemplate) => void;
  onDeleteTask: (task: TaskTemplate, status: ClaimStatusTemplate) => void;
  selectedPipelineId: number | undefined;
  onSelectPipeline: (id: number) => void;
  onCreateStatus: () => void;
  isAdmin: boolean;
}

export function DraggableStatusList({
  statuses,
  onEdit,
  onDelete,
  onReorder,
  onAddTask,
  onEditTask,
  onDeleteTask,
  selectedPipelineId,
  onSelectPipeline,
  onCreateStatus,
  isAdmin,
}: DraggableStatusListProps) {
  const [items, setItems] = useState(statuses);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { pipelines, updatePipeline, deletePipeline, isMutating } = useClaimPipelines();
  const activePipeline = pipelines.find((pipeline) => pipeline.id === selectedPipelineId);
  const canDeletePipeline = pipelines.length > 1 && !activePipeline?.isSystemDefault;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);

      // Update display order
      const reorderedItems = newItems.map((item, index) => ({
        ...item,
        displayOrder: index,
      }));

      setItems(reorderedItems);
      onReorder(reorderedItems);
    }
  };

  // Sync items with statuses prop changes
  useEffect(() => {
    setItems(statuses);
  }, [statuses]);

  const handleUpdatePipeline = async (draft: PipelineDraft) => {
    if (!activePipeline) return;
    await updatePipeline({
      id: activePipeline.id,
      data: { name: draft.name, description: draft.description || null, color: draft.color },
    });
    setDialogOpen(false);
  };

  const handleDeletePipeline = async () => {
    if (!activePipeline) return;
    await deletePipeline(activePipeline.id);
    setDeleteOpen(false);
    const remainingPipeline = pipelines.find((pipeline) => pipeline.id !== activePipeline.id);
    if (remainingPipeline) {
      onSelectPipeline(remainingPipeline.id);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        {activePipeline && (
          <div className="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-0.5">
              <span className="font-medium">{activePipeline.name}</span>
              {activePipeline.description && (
                <p className="truncate text-sm text-muted-foreground">{activePipeline.description}</p>
              )}
            </div>

            {isAdmin && (
              <div className="flex shrink-0 items-center gap-2">
                <Button type="button" variant="outline" size="icon" onClick={() => setDialogOpen(true)} disabled={isMutating} title="Edit workflow">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setDeleteOpen(true)}
                  disabled={!canDeletePipeline || isMutating}
                  title={canDeletePipeline ? "Delete workflow" : "You cannot delete the default or your only workflow"}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button type="button" className="gap-1.5" onClick={onCreateStatus}>
                  <Plus className="h-4 w-4" />
                  Add Status
                </Button>
              </div>
            )}
          </div>
        )}

        <DndContext
          sensors={isAdmin ? sensors : []}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((status) => (
              <DraggableStatusItem
                key={status.id}
                status={status}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddTask={onAddTask}
                onEditTask={onEditTask}
                onDeleteTask={onDeleteTask}
                isAdmin={isAdmin}
              />
            ))}
          </SortableContext>
        </DndContext>
      </CardContent>

      <PipelineDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pipeline={activePipeline}
        onSubmit={handleUpdatePipeline}
        isSaving={isMutating}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{activePipeline?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the workflow. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDeletePipeline();
              }}
              disabled={isMutating}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
